import { useState, useEffect, type FormEvent } from 'react';
import { useUser } from '@clerk/clerk-react';
import { X, Gift, TrendingUp, CheckCircle, Mail } from 'lucide-react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import type { CarGoal } from '../../types';
import { createPaymentIntent } from '../../lib/stripe';
import { SupporterVisibilityPicker } from './HintNamePicker';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  goal: CarGoal;
  onSuccess?: () => void;
}

const pk = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
const stripePromise = pk ? loadStripe(pk) : null;

// Inner form that uses Stripe hooks — must live inside <Elements>
interface CheckoutFormProps {
  amount: number;
  type: 'gift' | 'investment';
  goal: CarGoal;
  email: string;
  isGuest: boolean;
  name: string;
  onSuccess: (guestEmail: string | null) => void;
  onBack: () => void;
}

function CheckoutForm({ amount, type, goal, email, isGuest, name: _name, onSuccess, onBack }: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');
    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: window.location.origin,
        payment_method_data: email ? { billing_details: { email } } : undefined,
      },
    });
    if (stripeError) {
      setError(stripeError.message ?? 'Payment failed');
      setProcessing(false);
    } else {
      // Fire confirmation email (non-blocking)
      if (email) {
        fetch('/api/post-contribution-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            campaignTitle: goal.title,
            campaignId: goal.id,
            amount,
            type,
          }),
        }).catch(() => {/* non-fatal */});
      }
      onSuccess(isGuest ? email : null);
    }
  };

  const ownershipPercentage = type === 'investment'
    ? ((amount / goal.goal_amount) * 100).toFixed(2)
    : '0';

  return (
    <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
      {type === 'investment' && (
        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-blue-600" />
            <span className="font-medium text-blue-800 text-sm">Investment Details</span>
          </div>
          <p className="text-sm text-blue-700">
            You'll own <strong>{ownershipPercentage}%</strong> of this vehicle
          </p>
        </div>
      )}
      <PaymentElement />
      {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
      <div className="flex gap-3 mt-4">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 bg-indigo-600 text-white py-3 rounded-lg font-medium disabled:opacity-50 hover:bg-indigo-700 transition-colors"
        >
          {processing ? 'Processing...' : `Pay $${amount}`}
        </button>
      </div>
      <p className="text-xs text-gray-400 text-center mt-3">
        Test card: 4242 4242 4242 4242 · Any future date · Any CVC
      </p>
    </form>
  );
}

export function PaymentModal({ isOpen, onClose, goal, onSuccess }: PaymentModalProps) {
  const { user } = useUser();
  const [step, setStep] = useState<'select' | 'visibility' | 'checkout' | 'success'>('select');
  const [type, setType] = useState<'gift' | 'investment'>('gift');
  const [amount, setAmount] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [guestEmail, setGuestEmail] = useState<string | null>(null);

  // Reset state whenever modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setType('gift');
      setAmount('');
      setEmail('');
      setName('');
      setClientSecret('');
      setError('');
      setGuestEmail(null);
    }
  }, [isOpen]);

  const handlePaymentSuccess = async (paidAsGuestEmail: string | null) => {
    if (paidAsGuestEmail) {
      // Guest: auto-create account + send magic link, then show success screen
      fetch('/api/invite-supporter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: paidAsGuestEmail,
          name: name || undefined,
          campaignTitle: goal.title,
          campaignId: goal.id,
        }),
      }).catch(() => {/* non-fatal */});
      setGuestEmail(paidAsGuestEmail);
      setStep('success');
    } else {
      // Logged-in user: go straight to investor dashboard
      onSuccess?.();
      window.location.href = '/investor';
    }
  };

  // Called when user selects "Continue to Payment" on the select step
  const handleAmountContinue = () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 5) { setError('Minimum $5'); return; }
    setError('');
    setStep('visibility');
  };

  // Called when user confirms visibility choice
  const handleVisibilityConfirm = async (chosenDisplayName: string | null) => {
    const amt = parseFloat(amount);
    const resolvedEmail = email || (user?.emailAddresses?.[0]?.emailAddress ?? '');
    const resolvedName  = chosenDisplayName ?? (name || user?.fullName || '');
    setLoading(true);
    setError('');
    try {
      const { clientSecret: secret } = await createPaymentIntent(amt, goal.id, type, {
        contributorId: user?.id ?? '',
        email: resolvedEmail,
        name: resolvedName,
        campaignTitle: goal.title,
      });
      setClientSecret(secret);
      setStep('checkout');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error creating payment');
      setStep('select');
    } finally {
      setLoading(false);
    }
  };


  if (!isOpen) return null;

  const parsedAmount = parseFloat(amount);
  const ownershipPercentage = type === 'investment' && parsedAmount >= 5
    ? ((parsedAmount / goal.goal_amount) * 100).toFixed(2)
    : '0';

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            {goal.image_url && (
              <img
                src={goal.image_url}
                alt={goal.title}
                className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
              />
            )}
            <div className="min-w-0">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Supporting</p>
              <h3 className="font-bold text-gray-900 truncate">{goal.title}</h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-3 flex-shrink-0 p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step 1: Select type + amount */}
        {step === 'select' && (
          <div className="p-4 sm:p-6 space-y-5">
            {/* Goal progress */}
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>${(goal.current_amount ?? 0).toLocaleString()} raised</span>
                <span>${(goal.goal_amount ?? 0).toLocaleString()} goal</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(((goal.current_amount || 0) / goal.goal_amount) * 100, 100)}%` }}
                />
              </div>
            </div>

            {/* Type toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Contribution Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setType('gift')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    type === 'gift'
                      ? 'border-rose-500 bg-rose-50 text-rose-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <Gift className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-medium">Gift</div>
                  <div className="text-xs opacity-75">No return expected</div>
                </button>
                <button
                  type="button"
                  onClick={() => setType('investment')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    type === 'investment'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <TrendingUp className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-medium">Investment</div>
                  <div className="text-xs opacity-75">Get ownership %</div>
                </button>
              </div>
            </div>

            {/* Email + name inputs (guests only) */}
            {!user && (
              <div className="space-y-3">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="First Last"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">We'll create your FAM account and send you a link to track this.</p>
                </div>
              </div>
            )}

            {/* Amount input */}
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                Contribution Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  id="amount"
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); setError(''); }}
                  placeholder="0.00"
                  min="5"
                  step="0.01"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Minimum contribution: $5.00</p>
            </div>

            {/* Investment ownership preview */}
            {type === 'investment' && parsedAmount >= 5 && (
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-blue-800 text-sm">Investment Details</span>
                </div>
                <p className="text-sm text-blue-700">
                  You'll own <strong>{ownershipPercentage}%</strong> of this vehicle
                </p>
              </div>
            )}

            {error && (
              <p className="text-red-600 text-sm">{error}</p>
            )}

            <button
              type="button"
              onClick={handleAmountContinue}
              disabled={!amount || parsedAmount < 5}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {`Continue → Pay $${amount || '0'}`}
            </button>
          </div>
        )}

        {/* Loading state while creating payment intent after visibility pick */}
        {step === 'checkout' && loading && (
          <div className="p-6 text-center text-gray-500">
            <div className="inline-block w-6 h-6 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-sm">Setting up payment…</p>
          </div>
        )}

        {/* Step 3: Stripe Elements checkout */}
        {step === 'checkout' && clientSecret && !loading && (
          <Elements
            stripe={stripePromise}
            options={{ clientSecret, appearance: { theme: 'stripe' } }}
          >
            <CheckoutForm
              amount={parsedAmount}
              type={type}
              goal={goal}
              email={email || (user?.emailAddresses?.[0]?.emailAddress ?? '')}
              isGuest={!user}
              name={(name || user?.fullName) ?? ''}
              onSuccess={handlePaymentSuccess}
              onBack={() => setStep('select')}
            />
          </Elements>
        )}
      </div>

        {/* Step 4: Success screen */}
        {step === 'success' && (
          <div className="p-8 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 mb-2">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">You're in the FAM! 🎉</h3>
            <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">
              Your contribution to <span className="font-semibold text-gray-700">{goal.title}</span> was successful.
            </p>
            {guestEmail && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-left space-y-1">
                <div className="flex items-center gap-2 text-indigo-700 font-semibold text-sm">
                  <Mail className="w-4 h-4" />
                  Check your email
                </div>
                <p className="text-indigo-600 text-sm">
                  We sent a link to <strong>{guestEmail}</strong>. Click it to activate your account, track your contribution, and complete your profile.
                </p>
              </div>
            )}
            <button
              onClick={onClose}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
            >
              Done
            </button>
          </div>
        )}

      {/* Step 2: Visibility picker — renders as its own overlay above the modal */}
      <SupporterVisibilityPicker
        isOpen={step === 'visibility'}
        onConfirm={handleVisibilityConfirm}
        onClose={() => setStep('select')}
      />
    </div>
  );
}
